import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useEffect, useMemo, useRef, useState } from "react";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { createVehicleSearchEvent } from "../../api/dossier/createVehicleSearchEvent";
import { getDossier } from "../../api/dossier/getDossier";
import {
  patchVehicleSearchEvent,
  PatchVehicleSearchEventPayload,
} from "../../api/dossier/patchVehicleSearchEvent";
import { getListQuote } from "../../api/quote/getListQuote";
import { getPreviousVehicleSearchQuery } from "../../api/vehicle/getPreviousVehicleSearchQuery";
import { getVehicleAspects } from "../../api/vehicle/getVehicleAspects";
import { searchVehicleVariants } from "../../api/vehicle/searchVehicleVariants";
import { setPreviousVehicleSearchQuery } from "../../api/vehicle/setPreviousVehicleSearchQuery";
import APTabs, { APTabDetails } from "../../components/APTabs";
import APButton from "../../components/buttons/APButton";
import { APButtonLink } from "../../components/buttons/APButtonLink";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import InfoButton from "../../components/buttons/InfoButton";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../components/form/APAutoComplete";
import APForm from "../../components/form/APForm";
import APSlider, { APSliderRangeFilter } from "../../components/form/APSlider";
import PageContainer from "../../components/PageContainer";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import CopyExternalUrlField from "../../components/utils/CopyExternalUrlField";
import CopyMarketingUrl from "../../components/utils/CopyMarketingUrl";
import { parseMapFromConfig } from "../../helpers/configuration";
import { toThousands } from "../../helpers/currencies";
import { currentDate, toYearOnlyFormat } from "../../helpers/dates";
import { getCascadedValues } from "../../helpers/map";
import {
  buildVehicleAspectOptions,
  buildVehicleVariantSearchInputsFromParams,
  buildVehicleVariantSearchParams,
  buildVehicleVariantSearchQueryFromParams,
  isPowerPlantTypeElectric,
  VehicleVariantFieldLabelMap,
  VehicleVariantValueFormatters,
} from "../../helpers/vehicle";
import { useDebounce } from "../../hooks/useDebounce";
import { useEventTracking } from "../../hooks/useEventTracking";
import { useFaqs } from "../../hooks/useFaqs";
import { useAuth } from "../../providers/AuthProvider";
import { VEHICLE_BODY_TYPE_SEGMENT_MAP } from "../../types/Config";
import { BodyType, PowerPlantType, VehicleSegment } from "../../types/Vehicles";
import APStickyHeader from "./components/APStickyHeader";
import VehicleMatchCount from "./components/VehicleMatchCount";
import VehicleSearchFilterSummary from "./components/VehicleSearchFilterSummary";

export type VehicleSearchInputs = {
  year: AutoCompleteOption | null;
  priceRange: APSliderRangeFilter;
  segment: AutoCompleteOption[] | null;
  seatCapacity: AutoCompleteOption[] | null;
  powerPlantType: AutoCompleteOption[] | null;
  bodyType: AutoCompleteOption[] | null;
  minCargoVolumeRange: APSliderRangeFilter;
  powerKwRange: APSliderRangeFilter;
  fuelType: AutoCompleteOption[] | null;
  evRange: APSliderRangeFilter;
  fuelEconomyRange: APSliderRangeFilter;
  make: AutoCompleteOption[] | null;
  accelerationRange: APSliderRangeFilter;
  warrantyYearsRange: APSliderRangeFilter;
  ancapratingRange: APSliderRangeFilter;
  towingUnbrakedKgRange: APSliderRangeFilter;
  co2CombinedRange: APSliderRangeFilter;
  powerToWeightRange: APSliderRangeFilter;
  driveDescription: AutoCompleteOption[] | null;
  transmission: AutoCompleteOption[] | null;
};

type FieldVisibilityFlags = {
  powerPlantType: boolean;
  seatCapacity: boolean;
  make: boolean;
  evRange: boolean;
  fuelEconomyRange: boolean;
  fuelType: boolean;
  co2CombinedRange: boolean;
  additionalCriteria: boolean;
};

const DossierVehicleSearch = () => {
  const [hasPrefilledForm, setHasPrefilledForm] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [filterSummaryExpanded, setFilterSummaryExpanded] = useState(false);
  const [showResetButton, setShowResetButton] = useState(false);
  const [moreCriteriaExpanded, setMoreCriteriaExpanded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getScopedPageUrl, inAppScope } = useAuth();
  const vehicleMatchElementRef = useRef<HTMLElement>(null);
  const quoteId = searchParams.get("quoteId") || undefined;

  const { dossierId } = useParams();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isDirty },
    reset,
  } = useForm<VehicleSearchInputs>({
    mode: "onTouched",
  });
  const watchedValues = useWatch({ control });
  const initialValues = getValues();

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const dossier = getDossier(dossierId);
  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "vehicle-search-input",
    contextIds: {
      opportunityId: dossier.data?.opportunity?.id,
    },
  });

  const retrievePrimaryQuote = dossier.data?.opportunity?.primaryQuoteId
    ? true
    : false;
  const primaryQuote = getListQuote(
    dossier.data?.opportunity?.primaryQuoteId || "",
    { disabled: !retrievePrimaryQuote }
  );
  const previousSearchQuery = getPreviousVehicleSearchQuery();
  const { mutateAsync: saveSearchQuery } = setPreviousVehicleSearchQuery();

  const { mutateAsync: createVehicleSearchEventAsync } =
    createVehicleSearchEvent(dossierId);
  const { mutateAsync: patchVehicleSearchEventAsync } =
    patchVehicleSearchEvent(dossierId);

  const vehicleAspects = getVehicleAspects();
  const configMap = getPublicConfig();

  const _query =
    searchParams.size > 0 && !!searchParams.get("year")
      ? buildVehicleVariantSearchQueryFromParams(searchParams)
      : null;

  const {
    data: vehicleVariants,
    isFetching: isVehicleVariantsFetching,
    isLoading: isVehicleVariantsLoading,
  } = searchVehicleVariants(_query, {
    disabled: !_query,
    keepPreviousData: true,
  });

  if (dossier.isError) {
    throw dossier.error;
  }
  if (primaryQuote.isError) {
    throw primaryQuote.error;
  }
  if (vehicleAspects.isError) {
    throw vehicleAspects.error;
  }
  if (configMap.isError) {
    throw configMap.error;
  }

  const updateSearchParams = (inputs: VehicleSearchInputs) => {
    const params = buildVehicleVariantSearchParams(inputs);
    if (quoteId) {
      params.set("quoteId", quoteId);
    }
    setSearchParams(params, { replace: true });

    return params;
  };

  const _onSubmit: SubmitHandler<VehicleSearchInputs> = async () => {
    const params = updateSearchParams(watchedValues as VehicleSearchInputs);

    await saveSearchQuery(params.toString());

    onChangeVehicleSearchEvent(
      {
        lastSearchCriteria: JSON.stringify(watchedValues),
        lastSearchVehicleCount: vehicleVariants
          ? vehicleVariants.count
          : undefined,
        searchResultsViewed: true,
      },
      true
    );

    navigate({
      pathname: getScopedPageUrl("dossierVehicleSearchResults", dossierId),
      search: `?${params.toString()}${
        eventId ? `&searchEventId=${eventId}` : ""
      }`,
    });
  };

  const onToggleFilterSummary = () => {
    setFilterSummaryExpanded((expanded) => !expanded);
  };

  const onToggleMoreCriteria = () => {
    setMoreCriteriaExpanded((expanded) => !expanded);
  };

  // Debounce watchedValues
  const { debounced: debouncedValues, isDebouncing } = useDebounce(
    hasPrefilledForm ? watchedValues : null,
    250,
    hasPrefilledForm
  );

  const { onChange: onChangeVehicleSearchEvent, eventId } = useEventTracking<
    Omit<PatchVehicleSearchEventPayload, "vehicleSearchEventId">
  >({
    createEvent: inAppScope("external")
      ? createVehicleSearchEventAsync
      : () => Promise.resolve(""),
    patchEvent: inAppScope("external")
      ? (
          eventId: string,
          updatePayload: Omit<
            PatchVehicleSearchEventPayload,
            "vehicleSearchEventId"
          >
        ) => {
          patchVehicleSearchEventAsync({
            vehicleSearchEventId: eventId,
            ...updatePayload,
          });
          return Promise.resolve();
        }
      : () => Promise.resolve(),
    debounceTime: 3000,
  });

  useEffect(() => {
    if (debouncedValues) {
      updateSearchParams(debouncedValues as VehicleSearchInputs);

      // Clear pending when debouncing ends
      setIsPending(false);
    }
  }, [debouncedValues]);

  // Set pending when debouncing starts
  useEffect(() => {
    if (isDebouncing) {
      setIsPending(true);
    }
  }, [isDebouncing]);

  useEffect(() => {
    if (isDirty && !showResetButton) {
      setShowResetButton(true);
    }
  }, [isDirty]);

  useEffect(() => {
    if (vehicleVariants && isDirty && hasPrefilledForm) {
      const values = JSON.stringify(watchedValues);
      const count = vehicleVariants.count;

      onChangeVehicleSearchEvent({
        lastSearchCriteria: values,
        lastSearchVehicleCount: count,
      });
    }
  }, [vehicleVariants, isDirty, hasPrefilledForm]);

  const isLoading =
    dossier.isLoading ||
    primaryQuote.isLoading ||
    vehicleAspects.isLoading ||
    configMap.isLoading ||
    isLoadingFaqs;
  const isSuccess =
    dossier.isSuccess &&
    vehicleAspects.isSuccess &&
    configMap.isSuccess &&
    (!retrievePrimaryQuote || primaryQuote.isSuccess) &&
    isSuccessFaqs;

  const values = useMemo(
    () =>
      Object.keys(watchedValues || {}).length ? watchedValues : initialValues,
    [watchedValues, initialValues, isLoading]
  );
  const isUteOnly = useMemo(() => {
    return (
      watchedValues.bodyType?.length === 1 &&
      watchedValues.bodyType[0].id === "utility"
    );
  }, [watchedValues.bodyType]);

  const availableSegments = useMemo(() => {
    if (configMap.data && watchedValues.segment && hasPrefilledForm) {
      if (isUteOnly) {
        return [];
      }
      const bodyTypeSegmentMap = parseMapFromConfig(
        VEHICLE_BODY_TYPE_SEGMENT_MAP,
        configMap.data
      ) as Record<BodyType, VehicleSegment[]> | null;
      const bodyTypeIds = (watchedValues.bodyType as AutoCompleteOption[]).map(
        (bodyType) => bodyType.id as BodyType
      );

      // Don't filter segments if unmapped body type is selected
      if (bodyTypeSegmentMap && !bodyTypeIds.includes("unmapped")) {
        return getCascadedValues(bodyTypeIds, bodyTypeSegmentMap);
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  }, [configMap.data, watchedValues.bodyType, hasPrefilledForm]);

  const vehicleAspectOptions = useMemo(
    () =>
      vehicleAspects.data
        ? buildVehicleAspectOptions(
            vehicleAspects.data,
            availableSegments as VehicleSegment[] | undefined
          )
        : undefined,
    [vehicleAspects.data, watchedValues.segment]
  );
  const primaryQuotePriceDefaults = useMemo(() => {
    if (primaryQuote.data?.config.maxFinance) {
      const minPrice = Math.max(primaryQuote.data.config.maxFinance - 15000, 0);
      return { min: minPrice, max: primaryQuote.data.config.maxFinance };
    }
  }, [primaryQuote.data]);

  const resetToDefaultValues = (priceDefaults?: {
    min: number;
    max: number;
  }) => {
    if (vehicleAspectOptions) {
      const currentYear = toYearOnlyFormat(currentDate());

      const defaultValues: VehicleSearchInputs = {
        year:
          vehicleAspectOptions?.options.year.find(
            (year) => year.id === currentYear
          ) ??
          vehicleAspectOptions?.options.year[0] ??
          null,
        priceRange: {
          type: "between",
          min:
            priceDefaults?.min ??
            vehicleAspectOptions?.limits.listPriceIncGst.defaultStart ??
            0,
          max:
            priceDefaults?.max ??
            vehicleAspectOptions?.limits.listPriceIncGst.defaultEnd ??
            0,
        },
        bodyType: [],
        segment: [],
        seatCapacity: [],
        fuelEconomyRange: {
          type: "any",
        },
        make: [],
        accelerationRange: {
          type: "any",
        },
        warrantyYearsRange: {
          type: "any",
        },
        ancapratingRange: {
          type: "any",
        },
        towingUnbrakedKgRange: {
          type: "any",
        },
        co2CombinedRange: {
          type: "any",
        },
        powerToWeightRange: {
          type: "any",
        },
        driveDescription: [],
        powerPlantType: [],
        transmission: [],

        minCargoVolumeRange: {
          type: "any",
        },
        powerKwRange: {
          type: "any",
        },
        fuelType: [],
        evRange: {
          type: "any",
        },
      };

      reset(defaultValues);
      setShowResetButton(false);
      setMoreCriteriaExpanded(false);
    }
  };

  useEffect(() => {
    // Check if should wait for primary quote to load
    const waitForPrimaryQuote =
      !dossier.data || (retrievePrimaryQuote && !primaryQuote.data);

    if (
      vehicleAspectOptions &&
      !waitForPrimaryQuote &&
      previousSearchQuery.isSuccess
    ) {
      if (searchParams.get("year")) {
        const inputs = buildVehicleVariantSearchInputsFromParams(
          searchParams,
          vehicleAspectOptions
        );

        reset(inputs as VehicleSearchInputs);
        setShowResetButton(true);
      } else if (previousSearchQuery.data) {
        const inputs = buildVehicleVariantSearchInputsFromParams(
          new URLSearchParams(previousSearchQuery.data),
          vehicleAspectOptions
        );

        reset(inputs as VehicleSearchInputs);
        setShowResetButton(true);
      } else {
        resetToDefaultValues(primaryQuotePriceDefaults);
      }
      setHasPrefilledForm(true);
    }
  }, [
    vehicleAspects.data,
    dossier.data,
    primaryQuote.data,
    previousSearchQuery.isSuccess,
  ]);

  useEffect(() => {
    if (availableSegments && hasPrefilledForm) {
      let removeSegments: boolean = false;
      const newSegments: AutoCompleteOption[] = [];

      if (watchedValues.segment) {
        watchedValues.segment.forEach((segment) => {
          if (!availableSegments.includes(segment.id as VehicleSegment)) {
            removeSegments = true;
          } else {
            newSegments.push(segment as AutoCompleteOption);
          }
        });
      }

      if (removeSegments) {
        setValue("segment", newSegments as AutoCompleteOption[]);
      }
    }
  }, [availableSegments, hasPrefilledForm]);

  const renderHeaderContent = () => {
    return (
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            {(isVehicleVariantsLoading || vehicleVariants) &&
            showResetButton ? (
              renderAppLocation(
                "VehicleSearch-VehicleCount",
                <VehicleMatchCount
                  currentCount={vehicleVariants?.count ?? 0}
                  successThresholdCount={30}
                  warningThresholdCount={100}
                  isFetching={isVehicleVariantsFetching || isPending}
                  isLoading={isVehicleVariantsLoading}
                />
              )
            ) : (
              <Typography variant="labelLarge">
                Define your vehicle criteria to find your dream car.
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            {showResetButton && (
              <APButton
                text="Reset"
                color="primary"
                onClick={() => resetToDefaultValues(primaryQuotePriceDefaults)}
              />
            )}
            <APButton
              variant="contained"
              text="Search"
              color="secondary"
              onClick={handleSubmit(_onSubmit)}
              disabled={isVehicleVariantsLoading}
            />
          </Box>
        </Box>

        <Box>
          <VehicleSearchFilterSummary
            inputs={values as VehicleSearchInputs}
            valueFormatters={VehicleVariantValueFormatters}
            fieldLabelMap={VehicleVariantFieldLabelMap}
            expanded={filterSummaryExpanded}
            onExpandToggle={onToggleFilterSummary}
          />
        </Box>
      </Stack>
    );
  };

  const renderMoreCriteriaTabs = () => {
    const tabs: APTabDetails[] = vehicleAspectOptions
      ? [
          {
            label: "General",
            children: (
              <Stack spacing={2} mt={3}>
                <APSlider<VehicleSearchInputs>
                  name="ancapratingRange"
                  label={VehicleVariantFieldLabelMap.ancapratingRange}
                  control={control}
                  validations={{ required: true }}
                  defaultValue={{
                    type: "any",
                  }}
                  minValue={vehicleAspectOptions.limits.ancaprating.min}
                  maxValue={vehicleAspectOptions.limits.ancaprating.max}
                  step={vehicleAspectOptions.limits.ancaprating.step}
                  valueFormatter={
                    VehicleVariantValueFormatters.ancapratingRange
                  }
                  config={{
                    type: "single",
                    includeAll: "start",
                    alwaysGreaterThan: true,
                    closedUpperBound: true,
                  }}
                />
                <APSlider<VehicleSearchInputs>
                  name="warrantyYearsRange"
                  label={VehicleVariantFieldLabelMap.warrantyYearsRange}
                  control={control}
                  validations={{ required: true }}
                  defaultValue={{
                    type: "any",
                  }}
                  minValue={vehicleAspectOptions.limits.warrantyYears.min}
                  maxValue={vehicleAspectOptions.limits.warrantyYears.max}
                  step={vehicleAspectOptions.limits.warrantyYears.step}
                  valueFormatter={
                    VehicleVariantValueFormatters.warrantyYearsRange
                  }
                  config={{
                    type: "single",
                    includeAll: "start",
                    openUpperBound: true,
                    alwaysGreaterThan: true,
                  }}
                />

                {!isUteOnly && (
                  <APAutoComplete
                    name="segment"
                    label={VehicleVariantFieldLabelMap.segment}
                    options={vehicleAspectOptions.options.segment}
                    control={control}
                    validations={{}}
                    errors={errors}
                    defaultValue={[]}
                    setValue={setValue}
                    multiple
                    disabled={vehicleAspectOptions.options.segment.length === 0}
                  />
                )}

                {fieldVisibilityFlags.fuelType && (
                  <APAutoComplete
                    name="fuelType"
                    label={VehicleVariantFieldLabelMap.fuelType}
                    options={vehicleAspectOptions.options.fuelType}
                    control={control}
                    validations={{}}
                    errors={errors}
                    defaultValue={[]}
                    setValue={setValue}
                    multiple
                  />
                )}

                <APAutoComplete
                  name="transmission"
                  label={VehicleVariantFieldLabelMap.transmission}
                  options={vehicleAspectOptions.options.transmission}
                  control={control}
                  validations={{}}
                  errors={errors}
                  defaultValue={[]}
                  setValue={setValue}
                  multiple
                />
              </Stack>
            ),
          },
          {
            label: "Performance",
            children: (
              <Stack spacing={2} mt={3}>
                <APSlider<VehicleSearchInputs>
                  name="powerKwRange"
                  label={VehicleVariantFieldLabelMap.powerKwRange}
                  control={control}
                  validations={{ required: true }}
                  defaultValue={{
                    type: "any",
                  }}
                  minValue={vehicleAspectOptions.limits.powerKw.min}
                  maxValue={vehicleAspectOptions.limits.powerKw.max}
                  step={vehicleAspectOptions.limits.powerKw.step}
                  valueFormatter={VehicleVariantValueFormatters.powerKwRange}
                  config={{
                    type: "single",
                    includeAll: "start",
                    openUpperBound: true,
                    alwaysGreaterThan: true,
                  }}
                />

                <APSlider<VehicleSearchInputs>
                  name="accelerationRange"
                  label={VehicleVariantFieldLabelMap.accelerationRange}
                  control={control}
                  validations={{ required: true }}
                  defaultValue={{
                    type: "any",
                  }}
                  minValue={vehicleAspectOptions.limits.acceleration.min}
                  maxValue={vehicleAspectOptions.limits.acceleration.max}
                  step={vehicleAspectOptions.limits.acceleration.step}
                  valueFormatter={
                    VehicleVariantValueFormatters.accelerationRange
                  }
                  config={{
                    type: "single",
                    includeAll: "end",
                    alwaysLessThan: true,
                  }}
                />

                <APSlider<VehicleSearchInputs>
                  name="powerToWeightRange"
                  label={VehicleVariantFieldLabelMap.powerToWeightRange}
                  control={control}
                  validations={{ required: true }}
                  defaultValue={{
                    type: "any",
                  }}
                  minValue={vehicleAspectOptions.limits.powerToWeight.min}
                  maxValue={vehicleAspectOptions.limits.powerToWeight.max}
                  step={vehicleAspectOptions.limits.powerToWeight.step}
                  valueFormatter={
                    VehicleVariantValueFormatters.powerToWeightRange
                  }
                  config={{
                    type: "single",
                    includeAll: "start",
                    openUpperBound: true,
                    alwaysGreaterThan: true,
                  }}
                />

                {fieldVisibilityFlags.co2CombinedRange && (
                  <APSlider<VehicleSearchInputs>
                    name="co2CombinedRange"
                    label={VehicleVariantFieldLabelMap.co2CombinedRange}
                    control={control}
                    validations={{ required: true }}
                    defaultValue={{
                      type: "any",
                    }}
                    minValue={vehicleAspectOptions.limits.co2Combined.min}
                    maxValue={vehicleAspectOptions.limits.co2Combined.max}
                    step={vehicleAspectOptions.limits.co2Combined.step}
                    valueFormatter={
                      VehicleVariantValueFormatters.co2CombinedRange
                    }
                    config={{
                      type: "single",
                      includeAll: "end",
                      alwaysLessThan: true,
                    }}
                  />
                )}

                {fieldVisibilityFlags.evRange && (
                  <APSlider<VehicleSearchInputs>
                    name="evRange"
                    label={VehicleVariantFieldLabelMap.evRange}
                    control={control}
                    validations={{ required: true }}
                    defaultValue={{
                      type: "any",
                    }}
                    minValue={vehicleAspectOptions.limits.range.min}
                    maxValue={vehicleAspectOptions.limits.range.max}
                    step={vehicleAspectOptions.limits.range.step}
                    valueFormatter={VehicleVariantValueFormatters.evRange}
                    config={{
                      type: "single",
                      includeAll: "start",
                      openUpperBound: true,
                      alwaysGreaterThan: true,
                    }}
                  />
                )}

                {fieldVisibilityFlags.fuelEconomyRange && (
                  <APSlider<VehicleSearchInputs>
                    name="fuelEconomyRange"
                    label={VehicleVariantFieldLabelMap.fuelEconomyRange}
                    control={control}
                    validations={{ required: true }}
                    defaultValue={{
                      type: "any",
                    }}
                    minValue={vehicleAspectOptions.limits.fuelEconomy.min}
                    maxValue={vehicleAspectOptions.limits.fuelEconomy.max}
                    step={vehicleAspectOptions.limits.fuelEconomy.step}
                    valueFormatter={
                      VehicleVariantValueFormatters.fuelEconomyRange
                    }
                    config={{
                      type: "single",
                      includeAll: "end",
                      alwaysLessThan: true,
                    }}
                  />
                )}
              </Stack>
            ),
          },
          {
            label: "Load Capacity",
            children: (
              <Stack spacing={2} mt={3}>
                <APSlider<VehicleSearchInputs>
                  name="minCargoVolumeRange"
                  label={VehicleVariantFieldLabelMap.minCargoVolumeRange}
                  control={control}
                  validations={{ required: true }}
                  defaultValue={{
                    type: "any",
                  }}
                  minValue={vehicleAspectOptions.limits.minCargoVolume.min}
                  maxValue={vehicleAspectOptions.limits.minCargoVolume.max}
                  step={vehicleAspectOptions.limits.minCargoVolume.step}
                  valueFormatter={
                    VehicleVariantValueFormatters.minCargoVolumeRange
                  }
                  config={{
                    type: "single",
                    includeAll: "start",
                    openUpperBound: true,
                    alwaysGreaterThan: true,
                  }}
                />

                <APSlider<VehicleSearchInputs>
                  name="towingUnbrakedKgRange"
                  label={VehicleVariantFieldLabelMap.towingUnbrakedKgRange}
                  control={control}
                  validations={{ required: true }}
                  defaultValue={{
                    type: "any",
                  }}
                  minValue={vehicleAspectOptions.limits.towingUnbrakedKg.min}
                  maxValue={vehicleAspectOptions.limits.towingUnbrakedKg.max}
                  step={vehicleAspectOptions.limits.towingUnbrakedKg.step}
                  valueFormatter={
                    VehicleVariantValueFormatters.towingUnbrakedKgRange
                  }
                  config={{
                    type: "single",
                    includeAll: "start",
                    openUpperBound: true,
                    alwaysGreaterThan: true,
                  }}
                />

                <APAutoComplete
                  name="driveDescription"
                  label={VehicleVariantFieldLabelMap.driveDescription}
                  options={vehicleAspectOptions.options.driveDescription}
                  control={control}
                  validations={{}}
                  errors={errors}
                  defaultValue={[]}
                  setValue={setValue}
                  multiple
                />
              </Stack>
            ),
          },
        ]
      : [];

    return (
      <Box
        pt={3}
        mt={2}
        sx={{ borderTop: "1px solid", borderColor: "outlineVariant.main" }}
      >
        <APTabs tabs={tabs} />
      </Box>
    );
  };

  const fieldVisibilityFlags: FieldVisibilityFlags = useMemo(() => {
    const hasBodyType =
      !!watchedValues.bodyType && watchedValues.bodyType.length > 0;
    const hasPowerPlantType =
      !!watchedValues.powerPlantType && watchedValues.powerPlantType.length > 0;
    const hasSeatCapacity =
      !!watchedValues.seatCapacity && watchedValues.seatCapacity.length > 0;

    // Check if BEV only
    const isBevOnly =
      watchedValues.powerPlantType?.length === 1 &&
      watchedValues.powerPlantType[0].id === "bev";

    // Check if electric is excluded
    const powerPlantIds =
      watchedValues.powerPlantType?.map((powerPlant) => powerPlant.id) ?? [];
    const fuelTypeIds = watchedValues.fuelType?.map((fuel) => fuel.id) ?? [];

    const excludesElectricPowerPlant =
      powerPlantIds.length > 0 &&
      !powerPlantIds.some(
        (powerPlantId) =>
          powerPlantId &&
          isPowerPlantTypeElectric(powerPlantId as PowerPlantType)
      );
    const excludesElectricFuelType =
      fuelTypeIds.length > 0 &&
      !fuelTypeIds.some(
        (fuelTypeId) => fuelTypeId && ["electric"].includes(fuelTypeId)
      );

    const excludesElectric =
      excludesElectricPowerPlant || excludesElectricFuelType;

    // Check if any additional criteria are selected
    const additionalCriteriaSelected =
      (!!watchedValues.segment && watchedValues.segment.length > 0) ||
      (!!watchedValues.powerKwRange &&
        watchedValues.powerKwRange.type !== "any") ||
      (!!watchedValues.minCargoVolumeRange &&
        watchedValues.minCargoVolumeRange.type !== "any") ||
      (!!watchedValues.accelerationRange &&
        watchedValues.accelerationRange.type !== "any") ||
      (!!watchedValues.warrantyYearsRange &&
        watchedValues.warrantyYearsRange.type !== "any") ||
      (!!watchedValues.ancapratingRange &&
        watchedValues.ancapratingRange.type !== "any") ||
      (!!watchedValues.towingUnbrakedKgRange &&
        watchedValues.towingUnbrakedKgRange.type !== "any") ||
      (!!watchedValues.powerToWeightRange &&
        watchedValues.powerToWeightRange.type !== "any") ||
      (!!watchedValues.driveDescription &&
        watchedValues.driveDescription.length > 0) ||
      (!!watchedValues.transmission && watchedValues.transmission.length > 0) ||
      (!!watchedValues.fuelType && watchedValues.fuelType.length > 0) ||
      (!!watchedValues.co2CombinedRange &&
        watchedValues.co2CombinedRange.type !== "any") ||
      (!!watchedValues.evRange && watchedValues.evRange.type !== "any") ||
      (!!watchedValues.fuelEconomyRange &&
        watchedValues.fuelEconomyRange.type !== "any");

    if (additionalCriteriaSelected && !moreCriteriaExpanded) {
      setMoreCriteriaExpanded(true);
    }

    return {
      powerPlantType: hasBodyType,
      seatCapacity: hasPowerPlantType,
      make: hasSeatCapacity,
      evRange: hasSeatCapacity && !excludesElectric,
      fuelEconomyRange: hasSeatCapacity && !isBevOnly,
      fuelType: hasSeatCapacity && !isBevOnly,
      co2CombinedRange: hasSeatCapacity && !isBevOnly,
      additionalCriteria: hasSeatCapacity,
    };
  }, [watchedValues]);

  useEffect(() => {
    if (hasPrefilledForm) {
      if (!fieldVisibilityFlags.powerPlantType) {
        setValue("powerPlantType", []);
      }

      if (!fieldVisibilityFlags.seatCapacity) {
        setValue("seatCapacity", []);
      }

      if (!fieldVisibilityFlags.make) {
        setValue("make", []);
      }

      if (!fieldVisibilityFlags.additionalCriteria) {
        setValue("ancapratingRange", { type: "any" });
        setValue("warrantyYearsRange", { type: "any" });
        setValue("segment", []);
        setValue("fuelType", []);
        setValue("transmission", []);

        setValue("powerKwRange", { type: "any" });
        setValue("accelerationRange", { type: "any" });
        setValue("powerToWeightRange", { type: "any" });
        setValue("co2CombinedRange", { type: "any" });
        setValue("evRange", { type: "any" });
        setValue("fuelEconomyRange", { type: "any" });

        setValue("minCargoVolumeRange", { type: "any" });
        setValue("towingUnbrakedKgRange", { type: "any" });
        setValue("driveDescription", []);
      }
    }
  }, [
    fieldVisibilityFlags.powerPlantType,
    fieldVisibilityFlags.seatCapacity,
    fieldVisibilityFlags.make,
    fieldVisibilityFlags.evRange,
    fieldVisibilityFlags.fuelEconomyRange,
    fieldVisibilityFlags.fuelType,
    fieldVisibilityFlags.co2CombinedRange,
    fieldVisibilityFlags.additionalCriteria,
    hasPrefilledForm,
  ]);
  const renderAppLocation = faqProps.renderAppLocation;

  return (
    <PageContainer
      loading={isLoading}
      title="Vehicle Search"
      titleButton={
        isSuccess ? (
          <Box mr={1}>
            {renderAppLocation(
              "VehicleSearch-Page",
              <InfoButton />,
              true,
              true
            )}
          </Box>
        ) : undefined
      }
    >
      {isSuccess && vehicleAspectOptions && (
        <>
          <Box mb={2}>
            <RestrictedAppScope scope="internal">
              <CopyExternalUrlField text="Copy the URL below to share this search (this is a driver specific url):" />
            </RestrictedAppScope>
          </Box>

          <Box ref={vehicleMatchElementRef}>{renderHeaderContent()}</Box>

          <APStickyHeader anchorElement={vehicleMatchElementRef}>
            {renderHeaderContent()}
          </APStickyHeader>

          <APForm
            onSubmit={handleSubmit(_onSubmit)}
            submitText="Search"
            isLoading={false}
            submitButtonColor="secondary"
            minSpacing
            disableSubmit={isVehicleVariantsLoading}
            isError={Object.keys(errors).length > 0}
          >
            <APSlider<VehicleSearchInputs>
              name="priceRange"
              label={VehicleVariantFieldLabelMap.priceRange}
              control={control}
              validations={{ required: true }}
              defaultValue={{
                type: "any",
              }}
              minValue={vehicleAspectOptions.limits.listPriceIncGst.min}
              maxValue={vehicleAspectOptions.limits.listPriceIncGst.max}
              step={vehicleAspectOptions.limits.listPriceIncGst.step}
              valueFormatter={(value) => toThousands(value, true)}
              config={{
                type: "range",
                openUpperBound: true,
              }}
            />

            <APAutoComplete
              name="year"
              label={VehicleVariantFieldLabelMap.year}
              options={vehicleAspectOptions.options.year}
              control={control}
              validations={{ required: true }}
              errors={errors}
              defaultValue={null}
            />

            <APAutoComplete
              name="bodyType"
              label={VehicleVariantFieldLabelMap.bodyType}
              options={vehicleAspectOptions.options.bodyType}
              control={control}
              validations={{ required: true }}
              errors={errors}
              defaultValue={[]}
              setValue={setValue}
              multiple
            />

            {fieldVisibilityFlags.powerPlantType && (
              <APAutoComplete
                name="powerPlantType"
                label={VehicleVariantFieldLabelMap.powerPlantType}
                options={vehicleAspectOptions.options.powerPlantType}
                control={control}
                validations={{ required: true }}
                errors={errors}
                defaultValue={[]}
                setValue={setValue}
                multiple
              />
            )}

            {fieldVisibilityFlags.seatCapacity && (
              <APAutoComplete
                name="seatCapacity"
                label={VehicleVariantFieldLabelMap.seatCapacity}
                options={vehicleAspectOptions.options.seatCapacity}
                control={control}
                validations={{ required: true }}
                errors={errors}
                defaultValue={[]}
                setValue={setValue}
                multiple
              />
            )}

            {fieldVisibilityFlags.make && (
              <APAutoComplete
                name="make"
                label={VehicleVariantFieldLabelMap.make}
                options={vehicleAspectOptions.options.make}
                control={control}
                validations={{}}
                errors={errors}
                defaultValue={[]}
                setValue={setValue}
                multiple
              />
            )}

            {fieldVisibilityFlags.additionalCriteria &&
              !moreCriteriaExpanded && (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <APButtonLink
                    text="More criteria"
                    onClick={onToggleMoreCriteria}
                  />
                </Box>
              )}

            {fieldVisibilityFlags.additionalCriteria &&
              moreCriteriaExpanded && <Box>{renderMoreCriteriaTabs()}</Box>}
          </APForm>
          <Stack
            spacing={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              mt: 2,
              pt: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                columnGap: 1,
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Box
                  sx={{ display: "flex", columnGap: 1, alignItems: "center" }}
                >
                  <BackButton />
                </Box>
              </Box>
            </Box>
            <Box>
              <RestrictedAppScope scope="external">
                <APContactHelpButton
                  regardingOpportunityId={
                    dossier.data.opportunity?.id ?? undefined
                  }
                />
              </RestrictedAppScope>
            </Box>
            <CopyMarketingUrl type="dossierVehicleSearchInput" />
          </Stack>
        </>
      )}
    </PageContainer>
  );
};

export default DossierVehicleSearch;
